import { GetStaticProps } from 'next';
import { useEffect, useState } from 'react';
import cn from 'classnames';

import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';
import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { getApolloClient } from '@/utils/apollo-client';
import { transformMenus } from '@/utils/transformMenus';
import { withLayout } from '@/layout/Layout';

import { LayoutProps } from '@/layout/Layout/Layout.props';
import { PageProps } from '@/types/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Hero } from '@/components/common/Hero';
import Image from 'next/image';
import { Heading } from '@/components/typography';
import { Section } from '@/components/common/Section';
import { COMPANY_HISTORY_PAGE_QUERY } from '@/data/companyHistoryPageQuery';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { footerDataTransformer } from '@/utils/footerDataTransformer';

import { AnimatedCard } from '@/components/animation/AnimatedCard';
import { PageHead } from '@/components/common/PageHead';

const Page = ({ data }: PageProps & LayoutProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const {
    featuredImage,
    seo,
    historyPage: { historyList },
    hero: { hero },
  } = data.pageBy.translation;

  const featureImage = featuredImage?.node;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <PageHead
        title={seo?.title || hero?.title}
        seo={seo || {}}
        canonicalUrl={seo?.canonicalUrl}
        breadcrumbs={seo?.breadcrumbs}
      />
      {isMounted && (
        <>
          <Hero
            isAccent
            isFeatureImage
            className="pb-[72px] lg:pb-8"
            breadcrumbs={seo?.breadcrumbs}
          >
            <div className="grid gap-10 text-white dark:text-darkFullDark">
              <Heading tag="h1" content={hero?.title} color="white" />
              {hero?.descrPtion && (
                <p
                  className="text-base md:max-w-[94%] md:text-lg lg:max-w-[90%] lg:pb-20"
                  dangerouslySetInnerHTML={{ __html: hero?.descrPtion }}
                />
              )}
            </div>
            {featureImage && (
              <div className="hidden self-end lg:block">
                <Image
                  src={featureImage?.mediaItemUrl ?? ''}
                  alt={featureImage?.altText ?? ''}
                  width={featureImage?.mediaDetails?.width}
                  height={featureImage?.mediaDetails?.height}
                />
              </div>
            )}
          </Hero>
          {historyList.length > 0 && (
            <Section className="pb-0 pt-[60px] md:pb-0 md:pt-[100px] lg:pb-0">
              <ul className="pl-5 md:pl-0">
                {historyList.map((item, index) => {
                  const { date, description, image } = item;
                  return (
                    <li
                      key={index}
                      className="border-after relative flex flex-col gap-y-10 pb-[120px] pl-14 first:text-xl first:uppercase first:after:border-dashed first:after:border-accentMain first:after:opacity-100 last:pb-[60px] md:flex-row md:gap-x-28 md:pl-0 md:text-xxl md:after:left-1/2 md:first:text-h3 md:first:text-h3 md:last:pb-[100px] md:even:flex-row-reverse lg:gap-x-[196px] lg:gap-y-[200px]"
                    >
                      <AnimatedCard
                        ease={[0.12, 0.755, 0.0, 1]}
                        duration={1.2}
                        className={cn(
                          'flex flex-col gap-10 md:basis-1/2',
                          'relative before:absolute before:-top-1 before:h-8 before:w-8 before:rotate-45 before:bg-accentMain smOnly:before:-left-[4.5rem]',
                          {
                            'md:before:-right-14 md:before:translate-x-1/2 lg:before:-right-[98px]':
                              index % 2 === 0,
                            'md:before:-left-14 md:before:-translate-x-1/2 lg:before:-left-[98px]':
                              index % 2 !== 0,
                          },
                        )}
                      >
                        <Heading
                          tag="h3"
                          content={date}
                          color="accent-main"
                          className={`${
                            index % 2 === 0 ? 'md:self-end' : 'md:self-start'
                          } text-h3 md:text-h2Tablet lg:text-h1Tablet`}
                        />
                        <div
                          className="max-w-[96%] lg:max-w-[85%]"
                          dangerouslySetInnerHTML={{ __html: description }}
                        />
                      </AnimatedCard>
                      <AnimatedCard
                        className="md:basis-1/2"
                        duration={0.75}
                        initial={{
                          opacity: 0,
                          clipPath:
                            index % 2 === 0
                              ? 'polygon(0 0, 0 0, 0 100%, 0% 100%)'
                              : 'polygon(100% 0, 100% 0, 100% 100%, 100% 100%)',
                        }}
                        animate={{
                          opacity: 1,
                          clipPath:
                            index % 2 === 0
                              ? 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)'
                              : 'polygon(100% 0, 0 0, 0 100%, 100% 100%)',
                        }}
                      >
                        {image && (
                          <Image
                            src={image.mediaItemUrl}
                            alt={image.altText ?? ''}
                            width={image.mediaDetails.width ?? ''}
                            height={image.mediaDetails.height ?? ''}
                            className="rounded"
                          />
                        )}
                      </AnimatedCard>
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}
        </>
      )}
    </>
  );
};

export default withLayout(Page);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const client = getApolloClient();
  const location =
    locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;
  const language = locale?.toUpperCase();
  const { data } = await client.query({
    query: COMPANY_HISTORY_PAGE_QUERY,
    variables: {
      language,
    },
  });
  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });
  const footerLocation =
    locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data,
      menus,
      footerData,
      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  };
};
